import { computed, type Ref } from 'vue'
import { useGoogleAnalytics } from '@/shared/composables/useGoogleAnalytics/useGoogleAnalytics'
import { useIsHolder } from '@/shared/composables/useIsHolder'
import { paymentMethods } from '@/shared/constants/other.constants'
import { useCoreStore, useVisitorsStore } from '@/shared/store'
import type { VisitorType } from '@/shared/types/store.types'
import { collectHoldersPass, lastFour } from '../QuoteWidget.helpers'

export const useQuoteAnalytics = (visitors: Ref<VisitorType[]>) => {
  const gaService = useGoogleAnalytics()
  const visitorsStore = useVisitorsStore()
  const coreStore = useCoreStore()

  const entryType = visitorsStore.getAnalyticsEntryType
  const quoteNumber = computed(() => visitorsStore.getQuoteData?.quote || '')
  const initialReferrer = computed(() => coreStore.getInitialReferrer)
  const allVisitorsLength = computed(() => visitorsStore.visitorsList.length)
  const holdersPass = computed(() => collectHoldersPass(visitors.value))

  const sendViewQuote = () => {
    gaService.sendViewQuote(
      entryType,
      allVisitorsLength.value,
      quoteNumber.value,
      initialReferrer.value,
      holdersPass.value
    )
  }

  const sendAddPerson = () => {
    gaService.sendAddPerson(
      'your_quote',
      entryType,
      allVisitorsLength.value,
      quoteNumber.value,
      initialReferrer.value
    )
  }

  const sendEditPerson = (visitor: VisitorType) => {
    gaService.sendEditPerson(
      'your_quote',
      entryType,
      allVisitorsLength.value,
      quoteNumber.value,
      initialReferrer.value,
      useIsHolder(visitor.relationship) ? 'main' : 'additional',
      lastFour(visitor.passport),
      !!visitor.visaValidityDate
    )
  }

  const sendDeleteVisitor = (
    visitor: VisitorType,
    view: string,
    params: Record<string, string> = {}
  ) => {
    gaService.sendRemoveVisitor(
      'delete_person',
      view,
      visitor.nationalityName || '',
      visitor.relationshipName,
      !!visitor.visaValidityDate,
      lastFour(visitor.passport),
      params
    )
  }

  const sendDialogVisitor = (
    visitor: VisitorType,
    view: string,
    eventName: 'delete_person' | 'click_btn',
    params: Record<string, string> = {}
  ) => {
    gaService.sendRemoveVisitor(
      eventName,
      view,
      visitor.relationshipName || '',
      visitor.nationalityName || '',
      !!visitor.visaValidityDate,
      lastFour(visitor.passport),
      params
    )
  }

  const sendExpand = (visitor: VisitorType) => {
    gaService.sendExpandEventByQuote(
      entryType,
      allVisitorsLength.value,
      quoteNumber.value,
      initialReferrer.value,
      useIsHolder(visitor.relationship) ? 'main' : 'additional',
      lastFour(visitor.passport),
      !!visitor.visaValidityDate
    )
  }

  const sendLearnMore = (coverName: string) => {
    gaService.sendQuoteLearnMore(quoteNumber.value, coverName)
  }

  const sendPurchaseStart = (
    quoteNo: string,
    usdPrice: string | number,
    paymentMethod: number,
    visitorsCount: number,
    isInboundSelected: boolean,
    inboundPrice: number
  ) => {
    gaService.sendPurchaseStart(
      entryType,
      quoteNo,
      usdPrice,
      paymentMethods[paymentMethod],
      initialReferrer.value,
      visitorsCount,
      holdersPass.value,
      isInboundSelected,
      inboundPrice
    )
  }

  const sendInboundToggle = (isOn: boolean, inboundPrice: number) => {
    gaService.sendToggleEventInboundAddon(quoteNumber.value, inboundPrice, isOn)
  }

  const sendInboundView = () => {
    gaService.sendViewInboundAddon(quoteNumber.value)
  }

  const sendOnSaveVisitor = (
    event: 'added_new_person' | 'click_proceed',
    page: string,
    visitor: VisitorType
  ) => {
    gaService.sendEventOnChangePerson(
      event,
      entryType,
      page,
      visitor.nationalityName || '',
      visitor.relationshipName,
      lastFour(visitor.passport),
      !!visitor.visaValidityDate
    )
  }

  const sendQuoteClickLink = () => {
    gaService.sendQuoteClickLink(quoteNumber.value)
  }

  return {
    sendViewQuote,
    sendAddPerson,
    sendEditPerson,
    sendDeleteVisitor,
    sendDialogVisitor,
    sendExpand,
    sendLearnMore,
    sendPurchaseStart,
    sendInboundToggle,
    sendInboundView,
    sendOnSaveVisitor,
    sendQuoteClickLink,
  }
}
